//@ts-check
import {arrayOf, bool, func, node, number, object, objectOf, oneOfType, string, shape} from 'prop-types';

export const TemplateFilter = {};

export const TeaserPosition = {
  channelUuid: string,
  contentVersionSetUuid: string,
  propertyVersionSetUuid: string,
  position: number,
};

const renditionShape = {
  blurSigma: number,
  height: number,
  id: string,
  label: string,
  labelOrConvertedName: string,
  name: string,
  preciseHeight: number,
  preciseWidth: number,
  rendition: string,
  scaleWidth: number,
  url: string,
  uuid: string,
  width: number,
  x: number,
  y: number,
};

export const imageShape = {
  alt: string,
  attributes: object,
  caption: string,
  created: string,
  description: string,
  externalId: string,
  height: number,
  isGif: bool,
  mediaStatus: string,
  mediaType: string,
  metadata: string,
  original: oneOfType([bool, string]),
  originalUrl: string,
  renditions: objectOf(shape(renditionShape)),
  sinclairOwned: bool,
  title: string,
  uuid: string,
  width: number,
};

export const TeaserContent = {
  attributes: object,
  channelUuid: string,
  children: object,
  contentVersionSetUuid: string,
  imageHero: shape(imageShape),
  imageTeaser: shape(imageShape),
  parents: object,
  propertyVersionSetUuid: string,
  slug: string,
  summary: string,
  teaserVersionSetUuid: string,
  title: string,
  url: string,
};

/**
 * @typedef {object} ComponentConfiguration
 * @prop {ObjMap<String>} [childComponentMapping]
 * @prop {object} [componentConfig]
 * @prop {string} [componentInstanceId]
 * @prop {string} [componentType]
 * @prop {string} [dataType]
 * @prop {TemplateFilter} [filter]
 * @prop {string} [filterVersionSetUuid]
 * @prop {TeaserContent[]} [teasers]
 * @prop {string} [title]
 * @prop {React.ReactNode} [titleOverride]
 * @prop {string} uuid
 * @prop {number} [visibleCount]
 * @prop {string} [epgUuid]
 * @prop {bool} [readOnly]
 */

// The configuration used to hydrate one react component
export const componentConfiguration = {
  childComponentMapping: objectOf(string), // Map of internal names to persisted UUIDs
  componentConfig: object,                 // Config specific to this component type
  componentInstanceId: string,             // Temporary beckend identifier for the component
  componentType: string,                   // The name of the react type for this component
  dataType: string,                        // The type of data (and thereby the configurator) for this component
  filter: shape(TemplateFilter),           // The filter specification for this component
  filterVersionSetUuid: string,            // The filter UUID for this component
  teasers: arrayOf(shape(TeaserContent)),  // The teasers for this component
  title: string,                           // Title for this component, may be shown to users
  titleOverride: node,                     // Displayed title for this component, should show on render
  uuid: string.isRequired,                 // A unique identifier for this component
  visibleCount: number,                    // Number of items above the fold
  epgUuid: string,                         // EPG uuid for LinearFeedConfigurator
  readOnly: bool,                          // Whether or not a teaserlist can be added <to></to>
};

// Top-level display data for react-based page, analogous to presentation data
export const pageConfiguration = {
  components: objectOf(shape(componentConfiguration)), // A map of identifiers to configuration objects
  siteSlug: string,                                    // The site slug
  section: string,                                     // The section identifier for the configuration
  rootComponent: string,                               // The identifier (presentation key) for the root component
  uuid: string,                                        // Unique identifier for this confid (mapped to presentation)
  name: string,                                        // Human-readable name for this presentation
  data: object,                                        // Extra _unstructured_ configuration data from the server
};

/**
 * @typedef {componentConfiguration} configuratorProps
 */

export const configuratorProps = Object.assign({
}, componentConfiguration);

// a React class. Don't ask me why react doesn't have a type to match their most important thing.
export const reactComponent = func; //PropTypes.instanceOf(Component) || PropTypes.element;

// This is the API that will be provided from any pagebuilder-compatible renderer
export const componentApi = Object.assign({
  getComponent: func.isRequired,
  onNavigation: func,
  onConfigurationChange: func,
}, componentConfiguration);

export const configurator = {
  reactComponent,
  overlayComponent: reactComponent,
  propTypes: objectOf(func),
};

/**
 * @typedef {R.merge<{onConfigurationChange: function(configuratorProps): void}, configuratorProps>} configuratorApi
 */

export const configuratorApi = Object.assign({
  onConfigurationChange: func.isRequired,
}, configuratorProps);
